<template>
    <v-container class="pa-5 fill-height" fluid>
        <TeamDialog
            :is-open="teamDialog"
            :team="currentTeam"
            @on-close="closeTeamDialog"
        />
        <LocalizationDialog
            :is-open="localizationDialog"
            :show-teams="mapType == 1"
            :localization="currentLocalization"
            @on-close="closeLocalizationDialog"
        />
        <v-card width="100%" height="100%" class="d-flex flex-column" :loading="actionLoading">
            <v-card-title class="pa-0">
                <v-container fluid class="py-0">
                    <v-row align="center">
                        <v-col cols="auto">
                            <v-radio-group
                                v-model="mapType"
                                row
                                hide-details
                                class="ma-0 pa-0"
                                @change="changeMapType"
                            >
                                <v-radio
                                    :value="1"
                                    label="Klient"
                                />
                                <v-radio
                                    :value="2"
                                    label="Ekipa"
                                />
                            </v-radio-group>
                        </v-col>

                        <v-divider v-if="$vuetify.breakpoint.smAndUp" vertical />

                        <v-col v-if="mapType === 1" cols="12" sm="4" lg="3">
                            <v-autocomplete
                                v-model="customer"
                                :items="customers"
                                label="Klient"
                                item-text="name"
                                return-object
                                placeholder="Wybierz klienta..."
                                no-data-text="Brak danych..."
                                hide-details
                                outlined
                                dense
                                @change="changeCustomer"
                            />
                        </v-col>

                        <v-col v-else cols="12" sm="4" lg="3">
                            <v-autocomplete
                                v-model="team"
                                :items="teams"
                                label="Ekipa"
                                item-text="name"
                                return-object
                                placeholder="Wybierz ekipę..."
                                no-data-text="Brak danych..."
                                hide-details
                                outlined
                                dense
                                @change="changeTeam"
                            />
                        </v-col>


                        <v-fade-transition>
                            <v-col v-if="mapType === 1" cols="auto">
                                <v-checkbox
                                    v-model="hasTeamMarkers"
                                    label="Pokaż ekipy"
                                    hide-details
                                    class="ma-0 pa-0"
                                    :disabled="!hasCustomer"
                                    @change="toggleTeamMarkers"
                                />
                            </v-col>
                        </v-fade-transition>
                    </v-row>
                </v-container>
            </v-card-title>
            <v-card-text class="google-map-container flex-grow-1 pa-0">
                <div
                    ref="map"
                    class="google-map"
                />
                <v-slide-x-transition>
                    <div v-show="mapType === 2" class="google-map-legend">
                        <v-card class="pa-3">
                            <h4>Legenda:</h4>

                            <div v-for="(icon, index) in icons" :key="index" class="google-map-legend__item">
                                <img :src="icon.src" class="google-map-legend__pin">
                                <span>{{ icon.name }}</span>
                            </div>
                        </v-card>
                    </div>
                </v-slide-x-transition>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import store from '@/store'
import { googleMapsApiLoader, icons } from '@/utils/googleMapsApiLoader'
import MarkerClusterer from '@googlemaps/markerclustererplus'

import TeamDialog from './TeamDialog'
import LocalizationDialog from './LocalizationDialog'

import { errorHandler } from '@/utils/errorHandler'

import { appConfig } from '@/config'

import { isEmpty } from 'lodash'

export default {
    name: 'Map',
    components: {
        TeamDialog, LocalizationDialog
    },
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('outdoors/outdoorsMap/getTeams'),
            store.dispatch('outdoors/outdoorsMap/getCustomers')
        ])
            .then(responses => {
                next(vm => {
                    vm.teams = responses[0].data.results
                    vm.customers = responses[1].data.results
                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            mapType: 1,
            customers: [],
            customer: {},
            teams: [],
            team: {},
            currentTeam: {},
            localizations: [],
            localizationsCount: null,
            hasTeamMarkers: false,
            currentLocalization: {},
            teamDialog: false,
            localizationDialog: false,
            google: null,
            map: null,
            markers: [],
            markerClusterer: null,
            actionLoading: false,
            icons: icons
        }
    },
    computed: {
        hasCustomer() {
            return !isEmpty(this.customer)
        }
    },
    mounted() {
        googleMapsApiLoader({
            apiKey: appConfig.outdoors.map.apiKey,
            language: 'pl',
            region: 'PL',
            mapId: appConfig.outdoors.map.mapId,
        })
            .then(google => {
                this.google = google
                this.initMap()
            })
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        toggleTeamMarkers(boolean) {
            if (boolean) {
                this.$store.dispatch('outdoors/outdoorsMap/getTeams', {
                    query: `?customerId=${this.customer.id}`
                })
                    .then(response => {
                        this.addTeamsMarkers(response.data.results)
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
            } else {
                this.markers = this.markers.filter((marker, index) => {
                    if (marker.category === 'team') {
                        this.markers[index].setMap(null)
                    }

                    return marker.category !== 'team'
                })
            }
        },
        initMap() {
            const mapContainer = this.$refs.map

            this.map = new this.google.maps.Map(mapContainer, {
                mapId: appConfig.outdoors.map.mapId
            })

            this.centerMap()
        },
        changeMapType() {
            this.deleteAllMarkers()
            this.customer = {}
            this.team = {}
            this.hasTeamMarkers = false
        },
        changeCustomer(customer) {
            this.actionLoading = true

            this.deleteAllMarkers()

            this.hasTeamMarkers = false

            this.$store.dispatch('outdoors/outdoorsMap/getLocalizations', {
                query: `?customerId=${customer.id}`,
            })
                .then(response => {
                    this.localizations = response.data.results
                    this.localizationsCount = response.data.count
                    this.addLocalizationsMarkers()
                    this.centerMap()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })

        },
        changeTeam(team) {
            if (team) {
                this.actionLoading = true

                this.deleteAllMarkers()

                this.$store.dispatch('outdoors/outdoorsMap/getLocalizations', {
                    query: `?outdoorLocalizationTeam_id=${team.id}`,
                })
                    .then(response => {
                        this.localizations = response.data.results
                        this.localizationsCount = response.data.count
                        this.addTeamMarker()
                        this.addLocalizationsMarkers(false)
                    })
                    .catch(error => {
                        errorHandler(error)
                    })
                    .finally(() => {
                        this.actionLoading = false
                    })
            }
        },
        addTeamMarker() {
            if (this.team.latitude && this.team.longitude) {
                const marker = new this.google.maps.Marker({
                    position: {
                        lat: this.team.latitude,
                        lng: this.team.longitude
                    },
                    map: this.map,
                    category: 'team',
                    icon: this.icons.find(icon => icon.codeName === 'black').src
                })

                this.map.setZoom(9)
                this.map.panTo(
                    {
                        lat: this.team.latitude,
                        lng: this.team.longitude
                    }
                )

                marker.addListener('click', () => {
                    this.showTeamDialog(this.team)
                })

                this.markers.push(marker)
            } else {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: 'Ekipa nie ma wprowadzonych współrzędnych'
                })
            }
        },
        addTeamsMarkers(teams) {
            teams.forEach(team => {
                if (team.latitude && team.longitude) {
                    const marker = new this.google.maps.Marker({
                        position: {
                            lat: team.latitude,
                            lng: team.longitude
                        },
                        map: this.map,
                        category: 'team',
                        icon: this.icons.find(icon => icon.codeName === 'black').src
                    })

                    marker.addListener('click', () => {
                        this.showTeamDialog(team)
                    })

                    this.markers.push(marker)
                }

            })
        },
        addLocalizationsMarkers(handleClusterer = true) {
            const findIcon = (localization) => {
                const icon = this.icons.find(icon => icon.codeName === localization.customerCodeName)

                return icon ? icon.src : this.icons.find(icon => icon.codeName === 'default').src
            }

            this.localizations.forEach(localization => {
                if (localization.latitude && localization.longitude) {
                    const marker = new this.google.maps.Marker({
                        position: {
                            lat: localization.latitude,
                            lng: localization.longitude
                        },
                        map: this.map,
                        category: 'customer',
                        icon: findIcon(localization)
                    })

                    marker.addListener('click', () => {
                        this.showLocalizationDialog(localization.id)
                    })

                    this.markers.push(marker)
                }

            })

            if (handleClusterer) {
                this.markerClusterer = new MarkerClusterer(this.map, this.markers, appConfig.outdoors.map.markerClusterer)
            }
        },

        deleteAllMarkers() {
            if (this.markerClusterer) {
                this.markerClusterer.clearMarkers()
            }

            this.markers.forEach((item, index) => {
                this.markers[index].setMap(null)
            })

            this.markers = []
            this.localizations = []
            this.localizationsCount = null
        },
        centerMap() {
            const bounds = new this.google.maps.LatLngBounds(
                new this.google.maps.LatLng(appConfig.outdoors.map.countryBounds.southWestLat, appConfig.outdoors.map.countryBounds.southWestLng),
                new this.google.maps.LatLng(appConfig.outdoors.map.countryBounds.northEastLat, appConfig.outdoors.map.countryBounds.northEastLng) )

            this.map.fitBounds(bounds)
        },
        showTeamDialog(team) {
            this.currentTeam = team
            this.teamDialog = true
        },
        closeTeamDialog() {
            this.teamDialog = false
            setTimeout(() => {
                this.currentTeam = {}
            }, 500)
        },
        showLocalizationDialog(id) {
            this.actionLoading = true

            this.$store.dispatch('outdoors/outdoorsSettings/getLocalization', id)
                .then(response => {
                    this.currentLocalization = response.data
                    this.localizationDialog = true
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                })
        },
        closeLocalizationDialog() {
            this.localizationDialog = false

            setTimeout(() => {
                this.currentLocalization = {}
            }, 500)
        }
    }
}
</script>
